/**
 *
 * SearchControl
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { withLeaflet } from 'react-leaflet';
import {
  Button,
  InputGroup,
  FormControl,
  ListGroup,
  ListGroupItem,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Spinner from 'components/Spinner';
import IconButton from 'components/IconButton';
import CloseButton from 'components/CloseButton';
import { greenA100, grey700 } from 'styles/colors';
import minWidth from 'utils/minWidth';
import toggleState from 'utils/toggleState';

const Wrapper = styled.div`
  width: 229px;
  .input-group button.close.shift-inside {
    position: absolute;
    z-index: 10;
    margin-left: -32px;
    padding: 6px 10px;
  }
  .input-group-addon {
    padding: 0;
    button {
      height: 32px;
      width: 32px;
    }
  }
  .fa-globe {
    color: ${grey700};
    font-size: 18px;
  }
`;

const ResultItem = styled(ListGroupItem)`
  &.list-group-item {
    padding: 10px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    white-space: nowrap;
    span {
      overflow-x: hidden;
      text-overflow: ellipsis;
    }
    &.active,
    &.active:hover,
    &.active:focus {
      color: inherit;
      background-color: ${greenA100};
      border-color: ${greenA100};
    }
  }
`;

/* eslint-disable react/prefer-stateless-function */
class SearchControl extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { query: '', visible: minWidth() };
    this.reset();
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.toggleVisible = toggleState('visible').bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { visible } = this.state;
    if (visible && !prevState.visible) {
      const input = document.getElementById('search-location');
      input.focus();
    }
  }

  reset() {
    const { geosearch, clearSearch } = this.props;
    if (geosearch.results) {
      clearSearch();
    }
  }

  handleClear() {
    const { clearSearch } = this.props;
    clearSearch();
    this.setState({ query: '', visible: minWidth() });
  }

  handleSelect(item) {
    return () => {
      const { selectResult } = this.props;
      selectResult(item);
    };
  }

  handleChange(ev) {
    this.reset();
    this.setState({ query: ev.target.value });
  }

  handleSubmit(ev) {
    ev.preventDefault();
    const { doSearch, leaflet } = this.props;
    const bounds = leaflet.map.getBounds();
    const viewbox = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ];
    const { query } = this.state;
    const limit = minWidth() ? 5 : 3;
    doSearch({
      q: query,
      limit,
      viewbox,
    });
  }

  renderForm() {
    const { query } = this.state;
    const { geosearch } = this.props;
    const closeInside = !minWidth() && !geosearch.results;
    return (
      <form onSubmit={this.handleSubmit}>
        <InputGroup>
          <FormControl
            type="text"
            id="search-location"
            name="searchLocation"
            value={query}
            onChange={this.handleChange}
            onBlur={this.handleEvent}
            onKeyUp={this.handleEvent}
          />
          {closeInside && (
            <Button className="close shift-inside" onClick={this.handleClear}>
              x
            </Button>
          )}
          <InputGroup.Addon>
            {geosearch.results ? (
              <CloseButton onClose={this.handleClear} />
            ) : (
              <button type="submit">
                <FontAwesomeIcon icon="search" />
              </button>
            )}
          </InputGroup.Addon>
        </InputGroup>
      </form>
    );
  }

  static renderIcon(icon) {
    if (!icon) {
      return null;
    }
    if (icon === 'globe') {
      return <FontAwesomeIcon icon="globe" size="lg" />;
    }
    return <img src={icon} alt="" />;
  }

  renderResults() {
    const {
      geosearch: { searching, results, selected },
    } = this.props;
    if (!searching && !results) {
      return null;
    }
    return (
      <ListGroup id="search-results">
        {searching ? (
          <ListGroupItem>
            <Spinner />
          </ListGroupItem>
        ) : (
          results.map(item => (
            <ResultItem
              key={item.id}
              active={selected && item.id === selected.id}
              title={item.name}
              onClick={this.handleSelect(item)}
            >
              <span>{item.name}</span>
              {SearchControl.renderIcon(item.icon)}
            </ResultItem>
          ))
        )}
      </ListGroup>
    );
  }

  render() {
    const { visible } = this.state;
    return visible ? (
      <Wrapper>
        {this.renderForm()}
        {this.renderResults()}
      </Wrapper>
    ) : (
      <IconButton
        className="btn-fab"
        icon="search"
        onClick={this.toggleVisible}
        aria-label="Open search"
      />
    );
  }
}

SearchControl.propTypes = {
  leaflet: PropTypes.object.isRequired,
  geosearch: PropTypes.object.isRequired,
  doSearch: PropTypes.func.isRequired,
  clearSearch: PropTypes.func.isRequired,
  selectResult: PropTypes.func.isRequired,
};

export default withLeaflet(SearchControl);
